import PATHS, {
  PERSON_TAB,
  POSITION_TAB,
  TEAM_TAB,
  url,
} from "../consts/urlPaths";

type OptionalTabType = string | null | undefined;

type PersonLike = { aggregateId?: string | null | undefined };
type PositionLike =
  | string
  | { id?: string | null | undefined; aggregateId?: string | null | undefined };
type TeamLike = string | { id?: string | null | undefined } | null | undefined;

export function personDetailsPath(
  person: PersonLike,
  type?: OptionalTabType
): string {
  return url(PATHS.PERSON_DETAILS, {
    id: person.aggregateId,
    tabType: type || PERSON_TAB.DETAILS,
  });
}

export function positionDetailsPath(
  position: PositionLike,
  type?: OptionalTabType
): string {
  const id =
    typeof position === "string"
      ? position
      : position?.id || position?.aggregateId;
  return url(PATHS.POSITION_DETAILS, {
    id,
    tabType: type || POSITION_TAB.DETAILS,
  });
}

export function teamDetailsPath(
  team: TeamLike,
  type?: OptionalTabType,
  workspaceId?: string | null
): string | null {
  if (!team) {
    return null;
  }
  const teamId = typeof team === "string" ? team : team?.id; // handle group object or just the group id
  return url(PATHS.TEAM_DETAILS, {
    id: teamId,
    tabType: type || TEAM_TAB.DETAILS,
    // Only override the ambient/current-URL workspace when an explicit one
    // is passed (e.g. rendering a team that belongs to a different
    // workspace than the one currently in the URL); `url()` falls back to
    // the ambient workspace when this is undefined.
    workspaceId,
  });
}

export function userHomePath(type?: OptionalTabType): string {
  return url(
    PATHS.USER_HOME.replace(":tabType", type || PERSON_TAB.USER_DASHBOARD)
  );
}

type GetDefaultRoutePathArgs = {
  userId?: string | null | undefined;
  hasPerson?: boolean | undefined;
  isRestricted?: boolean | undefined;
  isOrgDesignBetaEnabled?: boolean | undefined;
};

// Every literal path segment getDefaultRoutePath can land a caller on. The
// e2e login helper (e2e/auth.setup.ts) imports this instead of hardcoding
// its own copy, and routing.test.ts exhaustively checks every branch below
// against it - so a new destination added to getDefaultRoutePath without
// updating this pattern fails a fast local test, not just a live e2e run
// against a real tenant (see TPP-2013 / #6036).
export const DEFAULT_ROUTE_PATH_PATTERN =
  /\/(teams|explore|user\/dashboard|home|org-design-home)([/?#]|$)/;

export const getDefaultRoutePath = ({
  userId,
  hasPerson,
  isRestricted,
  isOrgDesignBetaEnabled,
}: GetDefaultRoutePathArgs): string => {
  // checks will be removed once we have a better UI to represent User Home page for all types of users
  if (userId && isRestricted) {
    return url(PATHS.DIRECTORY_USER_HOME_BASE_PATH);
  }

  if (isRestricted) {
    return url(PATHS.EXPLORE);
  }

  if (userId) {
    return userHomePath();
  }

  // getRoutes only ever enables ORG_DESIGN_HOME for a user with no linked
  // Person record at all (`noUserId`, independent of disablePersonalisation).
  // Gate on that same raw signal here (`hasPerson`), not `userId`, which is
  // nulled by disablePersonalisation regardless of whether a Person exists -
  // otherwise a Person-having user on a disablePersonalisation + OD-beta
  // workspace gets routed to a route getRoutes leaves disabled.
  return !hasPerson && isOrgDesignBetaEnabled
    ? url(PATHS.ORG_DESIGN_HOME)
    : url(PATHS.TEAMS);
};
